import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  IconButton,
  Menu,
  MenuItem,
  Avatar,
  Typography,
  Box,
  Divider,
  ListItemIcon,
  ListItemText,
  Chip,
  CircularProgress,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import {
  Settings as SettingsIcon,
  Instagram as InstagramIcon,
  AccountCircle as AccountIcon,
  Link as LinkIcon,
  LinkOff as LinkOffIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";

const UserSettingsDropdown = () => {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState(null);
  const [instagramIntegration, setInstagramIntegration] = useState(null);
  const [loading, setLoading] = useState(false);
  const [connectingInstagram, setConnectingInstagram] = useState(false);
  const [disconnectDialog, setDisconnectDialog] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });

  const isMenuOpen = Boolean(anchorEl);

  useEffect(() => {
    loadInstagramIntegration();
  }, []);

  const loadInstagramIntegration = async () => {
    try {
      setLoading(true);
      const response = await fetch("/api/oauth/integrations", {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        const instagram = data.integrations?.find(
          (integration) =>
            integration.platform === "instagram" && integration.is_active
        );
        setInstagramIntegration(instagram || null);
      }
    } catch (error) {
      console.error("Error loading Instagram integration:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleConnectInstagram = async () => {
    try {
      setConnectingInstagram(true);

      // Get OAuth URL from backend
      const response = await fetch("/api/oauth/connect/instagram", {
        method: "GET",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
        },
      });

      console.log("OAuth connect response status:", response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error("OAuth connect error response:", errorText);
        throw new Error(
          `Failed to get Instagram OAuth URL: ${response.status} - ${errorText}`
        );
      }

      const data = await response.json();

      // Open popup window
      const popup = window.open(
        data.auth_url,
        "instagram-oauth",
        "width=500,height=600,scrollbars=yes,resizable=yes"
      );

      // Listen for popup messages
      const messageListener = async (event) => {
        if (event.origin !== window.location.origin) {
          return;
        }

        if (
          event.data.type === "OAUTH_SUCCESS" &&
          event.data.platform === "instagram"
          
        ) {
          // Remove listener
          console.log("Received OAuth success message:", event.data)
          window.removeEventListener("message", messageListener);

          // Complete the OAuth flow by saving the integration
          try {
            const completeResponse = await fetch(
              "/api/oauth/complete/instagram",
              {
                method: "POST",
                headers: {
                  Authorization: `Bearer ${localStorage.getItem(
                    "token"
                  )}`,
                  "Content-Type": "application/json",
                },
                body: JSON.stringify({
                  access_token: event.data.access_token,
                  username: event.data.username,
                  platform_user_id: event.data.platform_user_id,
                  user_info: event.data.user_info || {},
                }),
              }
            );

            if (completeResponse.ok) {
              setSnackbar({
                open: true,
                message: t("oauth.instagram.connectSuccess"),
                severity: "success",
              });
              await loadInstagramIntegration(); // Refresh integrations
            } else {
              throw new Error("Failed to complete Instagram connection");
            }
          } catch (error) {
            console.error("Error completing Instagram OAuth:", error);
            setSnackbar({
              open: true,
              message: t("oauth.instagram.connectError"),
              severity: "error",
            });
          }

          setConnectingInstagram(false);
        } else if (event.data.type === "OAUTH_ERROR") {
          // Remove listener
          window.removeEventListener("message", messageListener);

          console.error("Instagram OAuth error:", event.data.error);
          setSnackbar({
            open: true,
            message: t("oauth.instagram.connectError"),
            severity: "error",
          });
          setConnectingInstagram(false);
        }
      };

      window.addEventListener("message", messageListener);

      // Check if popup was closed manually
      const checkClosed = setInterval(() => {
        if (popup.closed) {
          clearInterval(checkClosed);
          window.removeEventListener("message", messageListener);
          setConnectingInstagram(false);
        }
      }, 1000);

      // Timeout after 5 minutes
      setTimeout(() => {
        if (!popup.closed) {
          popup.close();
        }
        clearInterval(checkClosed);
        window.removeEventListener("message", messageListener);
        setConnectingInstagram(false);
      }, 300000);
    } catch (error) {
      console.error("Error connecting Instagram:", error);
      setSnackbar({
        open: true,
        message: t("oauth.instagram.connectError"),
        severity: "error",
      });
      setConnectingInstagram(false);
    }
  };
  const handleDisconnectInstagram = async () => {
    try {
      const response = await fetch("/api/oauth/integrations/instagram", {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      if (response.ok) {
        setSnackbar({
          open: true,
          message: t(
            "oauth.instagram.disconnectSuccess",
            "Instagram disconnected successfully"
          ),
          severity: "success",
        });
        setInstagramIntegration(null);
      } else {
        throw new Error("Failed to disconnect Instagram");
      }
    } catch (error) {
      console.error("Error disconnecting Instagram:", error);
      setSnackbar({
        open: true,
        message: t(
          "oauth.instagram.disconnectError",
          "Failed to disconnect Instagram"
        ),
        severity: "error",
      });
    }
    setDisconnectDialog(false);
  };

  const isTokenExpired = (integration) => {
    if (!integration?.expires_at) return false;
    return new Date(integration.expires_at) <= new Date();
  };

  const expired = instagramIntegration && isTokenExpired(instagramIntegration);

  return (
    <>
      <IconButton
        onClick={handleMenuOpen}
        color="primary"
        size="large"
        aria-label={t("userSettings.title", "User Settings")}
        aria-controls={isMenuOpen ? "user-settings-menu" : undefined}
        aria-haspopup="true"
        aria-expanded={isMenuOpen ? "true" : undefined}
      >
        <SettingsIcon />
      </IconButton>

      <Menu
        id="user-settings-menu"
        anchorEl={anchorEl}
        open={isMenuOpen}
        onClose={handleMenuClose}
        MenuListProps={{
          "aria-labelledby": "user-settings-button",
        }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        <MenuItem disabled>
          <ListItemIcon>
            <AccountIcon />
          </ListItemIcon>
          <ListItemText
            primary={t("userSettings.title", "User Settings")}
            secondary={t("userSettings.subtitle", "Manage your account")}
          />
        </MenuItem>

        <Divider />

        {/* Instagram Integration Section */}
        <MenuItem onClick={handleMenuClose} disabled>
          <ListItemIcon>
            <InstagramIcon sx={{ color: "#E4405F" }} />
          </ListItemIcon>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              width: "100%",
            }}
          >
            <Typography variant="body2" fontWeight="medium">
              {t("oauth.instagram.title", "Instagram")}
            </Typography>
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}
            >
              {loading ? (
                <CircularProgress size={16} />
              ) : instagramIntegration ? (
                <>
                  <Typography variant="caption" color="textSecondary">
                    @{instagramIntegration.platform_username}
                  </Typography>
                  <Chip
                    label={
                      expired
                        ? t("oauth.status.expired", "Expired")
                        : t("oauth.status.connected", "Connected")
                    }
                    color={expired ? "error" : "success"}
                    size="small"
                    icon={expired ? <ErrorIcon /> : <CheckCircleIcon />}
                  />
                </>
              ) : (
                <Typography variant="caption" color="textSecondary">
                  {t("oauth.status.notConnected", "Not connected")}
                </Typography>
              )}
            </Box>
          </Box>
        </MenuItem>

        {/* Instagram Actions */}
        {!instagramIntegration ? (
          <MenuItem
            onClick={handleConnectInstagram}
            disabled={connectingInstagram}
          >
            <ListItemIcon>
              {connectingInstagram ? (
                <CircularProgress size={20} />
              ) : (
                <LinkIcon />
              )}
            </ListItemIcon>
            <ListItemText
              primary={t("oauth.instagram.connect", "Connect Instagram")}
            />
          </MenuItem>
        ) : (
          <>
            {expired && (
              <MenuItem
                onClick={handleConnectInstagram}
                disabled={connectingInstagram}
              >
                <ListItemIcon>
                  {connectingInstagram ? (
                    <CircularProgress size={20} />
                  ) : (
                    <RefreshIcon />
                  )}
                </ListItemIcon>
                <ListItemText
                  primary={t(
                    "oauth.instagram.reconnect",
                    "Reconnect Instagram"
                  )}
                />
              </MenuItem>
            )}
            <MenuItem
              onClick={() => {
                handleMenuClose();
                setDisconnectDialog(true);
              }}
            >
              <ListItemIcon>
                <LinkOffIcon color="error" />
              </ListItemIcon>
              <ListItemText
                primary={t(
                  "oauth.instagram.disconnect",
                  "Disconnect Instagram"
                )}
                primaryTypographyProps={{ color: "error" }}
              />
            </MenuItem>
          </>
        )}
      </Menu>

      {/* Disconnect confirmation dialog */}
      <Dialog
        open={disconnectDialog}
        onClose={() => setDisconnectDialog(false)}
      >
        <DialogTitle>
          {t("oauth.instagram.disconnectTitle", "Disconnect Instagram?")}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {t(
              "oauth.instagram.disconnectMessage",
              "Are you sure you want to disconnect your Instagram account? You'll need to reconnect to use Instagram features again."
            )}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDisconnectDialog(false)}>
            {t("common.cancel", "Cancel")}
          </Button>
          <Button
            onClick={handleDisconnectInstagram}
            color="error"
            variant="contained"
          >
            {t("oauth.instagram.disconnect", "Disconnect")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default UserSettingsDropdown;
